import { ReactElement, useState } from "react";
import { IViewerApi } from "@bronte/viewer-api/src/main";

import { Menu } from "../Menu";
import { TocDrawer } from "../TocDrawer";
import { SettingsDrawer } from "../SettingsDrawer";
import { NotebookDrawer } from "../NotesDrawer";
import { FlashcardsDrawer } from "../FlashcardsDrawer";
import { AudioBookDrawer } from "../AudioBookDrawer";
import { SearchDrawer } from "../SearchDrawer";

import { DrawerTypes, SpotlightAudio } from "../../shared/types";
import type { EtextWidgetTestConfig } from "../../shared/etextWidgetTestConfig";
import type { DevShellDeviceType } from "../../shared/deviceType";
import "./styles.scss";

interface IAsideBar {
  tocNodeHtml: string;
  viewerApi: IViewerApi;
  renderedId: string;
  annotationUpdated: boolean;
  parentActivityId?: string;
  onSpotlightClick: (data: SpotlightAudio) => void;
  onAudioPlayerToggle: () => void;
  showSpotlightAudio?: boolean;
  spotlightAudioDisabled?: boolean;
  hideAudioSettings?: boolean;
  minimalShell?: boolean;
  showDeviceTypeToggle?: boolean;
  deviceType?: DevShellDeviceType;
  onDeviceTypeChange?: (deviceType: DevShellDeviceType) => void;
  packagePath?: string;
  showTestWidgetButton?: boolean;
  onLoadTestWidget?: (config: EtextWidgetTestConfig) => void;
}

const AsideBar = ({
  annotationUpdated,
  tocNodeHtml,
  viewerApi,
  renderedId,
  parentActivityId,
  onSpotlightClick,
  onAudioPlayerToggle,
  showSpotlightAudio = false,
  spotlightAudioDisabled = false,
  hideAudioSettings = false,
  minimalShell = false as boolean,
  showDeviceTypeToggle = false,
  deviceType = "web",
  onDeviceTypeChange,
  packagePath = "",
  showTestWidgetButton = false,
  onLoadTestWidget,
}: IAsideBar): ReactElement => {
  const [showToc, setShowToc] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNotebook, setShowNotebook] = useState(false);
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [showAudioBook, setShowAudioBook] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const handleDrawers = (type: DrawerTypes | null): void => {
    switch (type) {
      case DrawerTypes.TOC:
        setShowSettings(false);
        setShowNotebook(false);
        setShowToc(!showToc);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.SETTINGS:
        setShowToc(false);
        setShowNotebook(false);
        setShowSettings(!showSettings);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.NOTEBOOK:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(!showNotebook);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.AUDIOBOOK:
        viewerApi?.media?.()?.pause?.();
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(false);
        setShowAudioBook(!showAudioBook);
        setShowSearch(false);
        break;
      case DrawerTypes.FLASHCARD:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(!showFlashcards);
        setShowAudioBook(false);
        setShowSearch(false);
        break;
      case DrawerTypes.SEARCH:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(false);
        setShowAudioBook(false);
        setShowSearch(!showSearch);
        break;
      default:
        setShowToc(false);
        setShowSettings(false);
        setShowNotebook(false);
        setShowFlashcards(false);
        setShowSearch(false);
        break;
    }
    viewerApi?.search().clear();
  };

  const closeSearchDrawer = (): void => {
    viewerApi?.search().clear();
    setShowSearch(false);
  };

  return (
    <div className="aside-bar">
      <Menu
        onMenuItemClick={handleDrawers}
        onAudioPlayerToggle={onAudioPlayerToggle}
        showSpotlightAudio={showSpotlightAudio}
        spotlightAudioDisabled={spotlightAudioDisabled}
        hideAudioSettings={hideAudioSettings}
        minimalShell={minimalShell}
        showDeviceTypeToggle={showDeviceTypeToggle}
        deviceType={deviceType}
        onDeviceTypeChange={onDeviceTypeChange}
      />

      {showToc && (
        <TocDrawer onClose={(): void => setShowToc(false)} renderedId={renderedId}>
          {tocNodeHtml}
        </TocDrawer>
      )}

      {showSettings && (
        <SettingsDrawer
          viewerApi={viewerApi}
          parentActivityId={parentActivityId}
          onClose={(): void => setShowSettings(false)}
          minimalShell={minimalShell}
          showTestWidgetButton={showTestWidgetButton}
          onLoadTestWidget={onLoadTestWidget}
        />
      )}

      {showNotebook && (
        <NotebookDrawer
          annotationUpdated={annotationUpdated}
          onClose={(): void => setShowNotebook(false)}
        />
      )}

      {showFlashcards && (
        <FlashcardsDrawer
          annotationUpdated={annotationUpdated}
          onClose={(): void => setShowFlashcards(false)}
        />
      )}

      {showAudioBook && (
        <AudioBookDrawer
          onSpotlightClick={onSpotlightClick}
          onClose={(): void => setShowAudioBook(false)}
        />
      )}

      {showSearch && (
        <SearchDrawer viewerApi={viewerApi} packagePath={packagePath} onClose={closeSearchDrawer} />
      )}
    </div>
  );
};

export default AsideBar;
